import apiService from './api.service';

// Types based on the schema from backend
export enum ItemType {
  AVATAR = 'avatar',
  GAME_UNLOCK = 'game_unlock',
  PLAY_TOKEN = 'play_token',
}

export interface ShopItem {
  _id: string;
  name: string;
  description: string;
  price: number;
  type: ItemType;
  isActive: boolean;
  imageUrl: string;
  sortOrder: number;
  gameId?: string;
  metadata?: Record<string, any>;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateShopItemDto {
  name: string;
  description: string;
  price: number;
  type: ItemType;
  imageUrl: string;
  sortOrder?: number;
  gameId?: string;
  metadata?: Record<string, any>;
}

export interface ShopItemsResponse {
  docs: ShopItem[];
  totalDocs: number;
  limit: number;
  totalPages: number;
  page: number;
  pagingCounter: number;
  hasPrevPage: boolean;
  hasNextPage: boolean;
  prevPage: number | null;
  nextPage: number | null;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
type CreateShopItemData = Omit<
  ShopItem,
  '_id' | 'createdAt' | 'updatedAt' | 'isActive'
>;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
type UpdateShopItemData = Partial<
  Omit<ShopItem, '_id' | 'createdAt' | 'updatedAt'>
>;

class ShopService {
  // Admin methods
  getAllItems(
    page = 1,
    limit = 10,
    type?: ItemType,
  ): Promise<ShopItemsResponse> {
    return apiService
      .get('/shop/items/all', { params: { page, limit, type } })
      .then((response) => response.data);
  }

  getItemById(id: string): Promise<ShopItem> {
    return apiService
      .get(`/shop/items/${id}`)
      .then((response) => response.data);
  }

  createItem(itemData: any): Promise<ShopItem> {
    // Remove isActive as it's not in the DTO
    const { isActive, ...itemToSend } = itemData;

    // Ensure numeric fields are properly typed
    if (itemToSend.price) itemToSend.price = Number(itemToSend.price);
    if (itemToSend.sortOrder)
      itemToSend.sortOrder = Number(itemToSend.sortOrder);

    // Handle metadata for PLAY_TOKEN type
    if (itemToSend.type === ItemType.PLAY_TOKEN && itemToSend.metadata) {
      itemToSend.metadata.tokenAmount = Number(itemToSend.metadata.tokenAmount);
    }

    console.log('Sending to API:', itemToSend);

    return apiService
      .post('/shop/items', itemToSend)
      .then((response) => response.data);
  }

  updateItem(id: string, itemData: any): Promise<ShopItem> {
    return apiService
      .put(`/shop/items/${id}`, itemData)
      .then((response) => response.data);
  }

  deleteItem(id: string): Promise<ShopItem> {
    return apiService
      .delete(`/shop/items/${id}`)
      .then((response) => response.data);
  }

  // Public methods
  getAvatars(limit = 10): Promise<ShopItem[]> {
    return apiService
      .get('/shop/avatars', { params: { limit } })
      .then((response) => response.data);
  }

  getGameUnlocks(limit = 10): Promise<ShopItem[]> {
    return apiService
      .get('/shop/game-unlocks', { params: { limit } })
      .then((response) => response.data);
  }

  getPlayTokens(limit = 10): Promise<ShopItem[]> {
    return apiService
      .get('/shop/play-tokens', { params: { limit } })
      .then((response) => response.data);
  }
}

const shopService = new ShopService();
export default shopService;
